import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, ShieldCheck, Clock } from "lucide-react";
import ApplyMobile from "@/components/ApplyMobile";
import useDocumentSeo from "@/lib/useDocumentSeo";

const PROD_URL = "https://model-workspace-1.emergent.host";
const CANONICAL = `${PROD_URL}/get-scouted`;

const SEO_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "url": CANONICAL,
  "name": "Get Scouted — Submit Your Modeling Profile | VOGUEVERVE",
  "description": "Let's find you some good work, darling. Start your modeling application in under 3 minutes — a swipe-through profile builder for fresh and pro models. Editorial, runway, campaign scouting worldwide.",
  "isPartOf": { "@id": `${PROD_URL}/#website` },
  "mainEntity": {
    "@type": "JobPosting",
    "title": "Model Scouting — Fresh Faces & Experienced Talent",
    "description": "VOGUEVERVE is scouting fresh faces and experienced models for editorial, runway, and campaign work worldwide.",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "VOGUEVERVE",
      "sameAs": PROD_URL,
    },
    "employmentType": ["CONTRACTOR", "FREELANCE"],
    "jobLocationType": "TELECOMMUTE",
    "applicantLocationRequirements": { "@type": "Country", "name": "Worldwide" },
    "datePosted": new Date().toISOString().split("T")[0],
    "directApply": true,
    "url": CANONICAL,
  },
};

export default function GetScouted() {
  const [open, setOpen] = useState(false);

  useDocumentSeo({
    title: "Get Scouted — Submit Your Modeling Profile | VOGUEVERVE",
    description: "Let's find you some good work, darling. Start your modeling application in under 3 minutes — a swipe-through profile builder for fresh and pro models. Editorial, runway, campaign scouting worldwide.",
    canonical: CANONICAL,
    ogImage: `${PROD_URL}/og-image.jpg`,
    keywords: "get scouted, model application, modeling agency application, submit modeling portfolio, become a model, fresh face casting, runway casting, editorial modeling, VOGUEVERVE apply",
    jsonLd: SEO_JSONLD,
  });

  return (
    <>
      <main data-testid="get-scouted-page" className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        {/* Slim header */}
        <header className="border-b border-white/10">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
            <Link to="/" data-testid="gs-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors">
              <ArrowLeft size={14} /> Back
            </Link>
            <span className="font-display text-lg md:text-xl font-black tracking-tighter uppercase">
              Vogue<span className="text-white/70">Verve</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 hidden sm:inline">
              Scouting · Open Call
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="flex-1 flex flex-col justify-center px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-5xl mx-auto w-full">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-[10px] md:text-xs uppercase tracking-[0.35em] font-bold text-white/50 mb-6"
            >
              Open Scouting · Worldwide
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="font-script text-white text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.9] mb-6 md:mb-8"
            >
              let&rsquo;s find you
              <br />
              some good work,
              <br className="hidden sm:block" />
              <span className="italic">darling.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="max-w-2xl text-lg md:text-xl text-white/60 leading-relaxed mb-10 md:mb-14"
            >
              A swipe-through profile builder for fresh faces and working models. Editorial. Runway. Campaigns. Under three minutes. Reviewed by real scouts.
            </motion.p>

            {/* Primary CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <button
                data-testid="gs-start-btn"
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-4 rounded-full bg-white text-black px-8 md:px-10 py-5 md:py-6 text-xs md:text-sm uppercase tracking-[0.25em] font-bold hover:bg-white/85 transition-colors active:scale-[0.98]"
              >
                Start my application
                <span className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center group-hover:translate-x-1 transition-transform">
                  <ArrowRight size={14} strokeWidth={2.5} />
                </span>
              </button>
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40">
                Free · No agent fees
              </span>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.85 }}
              className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 max-w-3xl"
            >
              {[
                { icon: Clock, k: "Under 3 min", v: "10 quick screens. No long forms." },
                { icon: ShieldCheck, k: "Reviewed by hand", v: "Every profile seen by a real scout." },
                { icon: Sparkles, k: "Fresh & pro welcome", v: "No experience? Still apply. We look for face, not resume." },
              ].map((it) => (
                <div key={it.k} className="border-t border-white/15 pt-5">
                  <it.icon size={16} strokeWidth={1.6} className="text-white/60 mb-3" />
                  <p className="font-display text-lg md:text-xl leading-tight mb-1">{it.k}</p>
                  <p className="text-xs md:text-sm text-white/50 leading-relaxed">{it.v}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        <footer className="border-t border-white/10 py-6">
          <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
              &copy; {new Date().getFullYear()} VOGUEVERVE Model Management
            </p>
            <div className="flex gap-6">
              <Link to="/legal#terms" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Terms</Link>
              <Link to="/legal#privacy" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Privacy</Link>
              <a href="tel:+910000000000" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">Support</a>
            </div>
          </div>
        </footer>
      </main>

      <ApplyMobile open={open} onClose={() => setOpen(false)} hideOnDesktop={false} />
    </>
  );
}
